import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth, useClerk } from "@clerk/clerk-react";
import { api, setAuthTokenGetter, clearPreviewEvent } from "@/lib/api";

// Dev-only bypass. When true, Clerk is skipped and the internal persona
// switcher drives identity. MUST be false in production.
export const DEMO_MODE =
  String(process.env.REACT_APP_DEMO_MODE || "false").toLowerCase() === "true";

const AuthContext = createContext(null);
export const useAuthCtx = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const clerk = useAuth();
  const { signOut } = useClerk();
  const isLoaded = DEMO_MODE ? true : clerk.isLoaded;
  const isSignedIn = DEMO_MODE ? true : clerk.isSignedIn;

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(!DEMO_MODE);

  // Register the Clerk token getter so axios attaches a Bearer token.
  useEffect(() => {
    if (!DEMO_MODE) setAuthTokenGetter(clerk.getToken);
  }, [clerk.getToken]);

  const refreshProfile = useCallback(async () => {
    if (DEMO_MODE) {
      setProfileLoading(false);
      return;
    }
    if (!isSignedIn) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }
    setProfileLoading(true);
    try {
      const p = await api.authMe();
      setProfile(p);
    } catch (e) {
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  }, [isSignedIn]);

  useEffect(() => {
    if (!isLoaded) return;
    refreshProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isSignedIn]);

  const doSignOut = useCallback(async () => {
    try {
      if (!DEMO_MODE) await signOut();
    } catch (e) {
      /* ignore */
    }
    setProfile(null);
    clearPreviewEvent();   // drop any preview-event selection on sign-out
    window.location.assign("/");
  }, [signOut]);

  const value = {
    demoMode: DEMO_MODE,
    clerkLoaded: isLoaded,
    isSignedIn: !!isSignedIn,
    profile,
    profileLoading: DEMO_MODE ? false : profileLoading,
    authorized: DEMO_MODE ? true : !!profile?.authorized,
    isAttendee: DEMO_MODE ? true : !!profile?.is_attendee,
    isManager: DEMO_MODE ? true : !!profile?.is_manager,
    hasAccess: DEMO_MODE ? true : !!profile?.has_access,
    dualAccess: DEMO_MODE ? true : !!profile?.dual_access,
    defaultDestination: DEMO_MODE ? "attendee_app" : (profile?.default_destination || "unauthorized"),
    roles: profile?.roles || [],
    attendee: profile?.attendee || null,
    email: profile?.email || null,
    reason: profile?.reason || null,
    refreshProfile,
    signOut: doSignOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
